import React, { useLayoutEffect, useMemo } from 'react'
import { t } from 'src/translations'
import {
  AdvancedMarker as GoogleMapsMarker,
  InfoWindow,
  Pin,
  useAdvancedMarkerRef
} from '@vis.gl/react-google-maps'
import {
  UpcomingSchedules,
  DiningOptionBehavior,
  RestaurantLocationsQuery
} from 'src/apollo/onlineOrdering'
import { getTakingOrdersTillString } from 'src/lib/js/schedule'

type OORestaurant = NonNullable<
  NonNullable<RestaurantLocationsQuery['restaurants']>[0]
> & { __typename: 'Restaurant' }

type InfoProps = {
  restaurant: OORestaurant
  selectedDiningOption?: DiningOptionBehavior
}

type Props = {
  position: google.maps.LatLng | google.maps.LatLngLiteral
  restaurant: OORestaurant
  onClick: () => void
  onClose?: () => void
  isInfoOpen?: boolean
  isSelected?: boolean
  selectedDiningOption?: DiningOptionBehavior
}

const DetailMarker = ({ restaurant, selectedDiningOption }: InfoProps) => {
  const isOpen = useMemo(
    () =>
      (selectedDiningOption === DiningOptionBehavior.Delivery
        ? restaurant.schedule.asapAvailableForDelivery
        : restaurant.schedule.asapAvailableForTakeout) ?? false,
    [selectedDiningOption, restaurant]
  )
  const diningBehaviorSchedule: UpcomingSchedules | undefined =
    restaurant.schedule.upcomingSchedules?.filter(
      (schedule) => schedule.behavior == selectedDiningOption
    )[0]
  const timeZoneId = useMemo(
    () =>
      restaurant.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone,
    [restaurant]
  )

  return (
    <div className='mapInfoWindow detail'>
      <h3>{restaurant.whiteLabelName || restaurant.name}</h3>
      <hr />
      <h4>{t('detail-marker.address')}</h4>
      <div>
        <div>
          {restaurant.location.address1}
          {restaurant.location.address2
            ? `, ${restaurant.location.address2}`
            : null}
        </div>
        <div>
          {restaurant.location.city}, {restaurant.location.state}{' '}
          {restaurant.location.zip}
        </div>
      </div>
      <div className='schedule'>
        {getTakingOrdersTillString(isOpen, diningBehaviorSchedule, timeZoneId)}
      </div>
    </div>
  )
}

const Marker = (props: Props) => {
  const [markerRef, marker] = useAdvancedMarkerRef()

  useLayoutEffect(() => {
    if (!marker) return

    marker.element.setAttribute('aria-haspopup', 'dialog')
  }, [marker])

  return (
    <>
      <GoogleMapsMarker
        ref={markerRef}
        key={props.restaurant.guid}
        position={props.position}
        onClick={props.onClick}
        title={t('fulfillment-marker.view-location-details', {
          location: props.restaurant.name
        })}
      >
        <Pin />
      </GoogleMapsMarker>
      {props.isInfoOpen ? (
        <InfoWindow anchor={marker} onClose={props.onClose} shouldFocus={true}>
          <DetailMarker
            restaurant={props.restaurant}
            selectedDiningOption={props.selectedDiningOption}
          />
        </InfoWindow>
      ) : null}
    </>
  )
}

export default Marker
